import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import DatePicker from "../../components/DatePicker";
import { Plus, X, AlertCircle, CheckCircle2, ArrowLeft, Eye } from "lucide-react";
import { ShopReviewCard } from "./ShopReviewCard";

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

export const blankQuoteSlot = () => ({
  tyre_brand: "",
  tyre_model: "",
  speed_rating: "",
  tyre_fitment_cost: "",
  alignment_cost: "",
  rotation_cost: "",
  stock_availability: "in_stock",
  service_date_estimate: null,
  quote_expiry: new Date(Date.now() + FOURTEEN_DAYS_MS),
  notes: "",
  // UI-only state
  _error: null,
  _submitting: false,
  _submitted: false,
  _skipped: false,
});

/**
 * Side-by-side multi-quote dialog.
 * Each card is an independent quote — validated and posted independently.
 * Successful cards are removed; failed cards stay open with their error.
 */
export function ShopQuoteFormDialog({
  quoteDialogOpen,
  setQuoteDialogOpen,
  selectedRequest,
  quoteForms,
  setQuoteForms,
  pricesIncludeGst,
  setPricesIncludeGst,
  submittingQuote,
  handleSubmitAllQuotes,
  handlePreValidateQuotes,
  handleSaveDraft,
  resetMultiQuoteForms,
  getServiceLabel,
}) {
  const [mode, setMode] = useState("edit"); // "edit" | "review"
  const [gstHighlight, setGstHighlight] = useState(false);

  // Reset to edit mode whenever the dialog opens or the request changes,
  // and pulse the GST toggle for ~3.5s so shops notice they can flip it.
  useEffect(() => {
    if (quoteDialogOpen) {
      setMode("edit");
      setGstHighlight(true);
      const t = setTimeout(() => setGstHighlight(false), 3500);
      return () => clearTimeout(t);
    }
  }, [quoteDialogOpen, selectedRequest?.request_id]);

  const isTyreService =
    selectedRequest?.service_type?.includes("tyres") ||
    selectedRequest?.selected_services?.includes("tyres");

  // Build the canonical tyre size string (e.g. "265/35R22") for the review
  // cards. Returns "" when the request isn't tyre-related or specs missing.
  const tyreSizeStr = (() => {
    const ts = selectedRequest?.tyre_specs;
    if (!ts || !ts.width) return "";
    const profile = ts.aspect_ratio || ts.profile;
    if (!profile || !ts.rim_size) return "";
    return `${ts.width}/${profile}R${ts.rim_size}`;
  })();
  const hasAlignment =
    selectedRequest?.service_type?.includes("alignment") ||
    selectedRequest?.service_type === "alignment_only" ||
    selectedRequest?.selected_services?.includes("alignment");
  const hasRotation =
    selectedRequest?.service_type === "rotation_only" ||
    selectedRequest?.selected_services?.includes("rotation");

  const maxQuotes = isTyreService ? 5 : 1;
  const remainingSlots = Math.max(
    0,
    maxQuotes - (selectedRequest?.shop_quotes_submitted || 0) - (quoteForms?.length || 0),
  );

  const updateSlot = (index, patch) => {
    setQuoteForms((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  const addSlot = () => {
    if (remainingSlots <= 0) return;
    setQuoteForms((prev) => [...prev, blankQuoteSlot()]);
  };

  const removeSlot = (index) => {
    setQuoteForms((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  // ---------- Mobile snap-scroll dot indicators ----------
  const scrollerRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardWidth = el.firstChild?.offsetWidth || el.clientWidth;
      const idx = Math.round(el.scrollLeft / cardWidth);
      setActiveDot(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [quoteForms?.length]);

  return (
    <Dialog
      open={quoteDialogOpen}
      onOpenChange={(open) => {
        setQuoteDialogOpen(open);
        if (!open) resetMultiQuoteForms();
      }}
    >
      <DialogContent
        className="max-w-[1500px] w-[100vw] sm:w-[98vw] h-[100vh] sm:h-[95vh] overflow-hidden p-0 flex flex-col rounded-none sm:rounded-lg"
        data-testid="multi-quote-dialog"
      >
        {/* ----- HEADER (compact single-row banner) ----- */}
        <DialogHeader className="px-4 sm:px-5 py-2 border-b shrink-0 space-y-1.5">
          {/* Row 1: title + meta + GST toggle (all inline) */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <DialogTitle className="font-heading text-base sm:text-lg leading-tight">
              {mode === "review" ? "Review " : "Quote "}
              {mode === "review"
                ? `${quoteForms?.filter((q) => !q._submitted && !q._skipped).length || 0} ${
                    (quoteForms?.filter((q) => !q._submitted && !q._skipped).length || 0) === 1
                      ? "quote"
                      : "quotes"
                  } · `
                : ""}
              <span className="text-primary">
                {selectedRequest?.request_number || `#${selectedRequest?.request_id}`}
              </span>
            </DialogTitle>
            <DialogDescription className="text-xs leading-tight m-0">
              {mode === "review" ? (
                "Spot any pricing typos before sending."
              ) : (
                <>
                  {selectedRequest && getServiceLabel(selectedRequest)}
                  {selectedRequest?.distance_km != null
                    ? ` · ${selectedRequest.distance_km.toFixed(1)} km`
                    : ""}{" "}
                  · up to {maxQuotes} {maxQuotes === 1 ? "quote" : "quotes"}
                </>
              )}
            </DialogDescription>

            {/* GST toggle pushed to right */}
            <div className="ml-auto">
              {mode === "edit" ? (
                <div
                  className={`flex items-center gap-1.5 rounded-md px-2 py-1 transition-all duration-500 ${
                    gstHighlight
                      ? "ring-2 ring-primary bg-primary/10 animate-pulse shadow-[0_0_0_4px_rgba(249,115,22,0.15)]"
                      : ""
                  }`}
                  data-testid="gst-toggle-wrapper"
                >
                  <Checkbox
                    id="prices_include_gst"
                    checked={pricesIncludeGst}
                    onCheckedChange={(checked) => setPricesIncludeGst(!!checked)}
                    data-testid="prices-include-gst-toggle"
                    className={gstHighlight ? "border-primary" : ""}
                  />
                  <Label
                    htmlFor="prices_include_gst"
                    className={`text-xs cursor-pointer whitespace-nowrap ${
                      gstHighlight ? "font-semibold text-primary" : ""
                    }`}
                  >
                    {pricesIncludeGst ? "Incl. GST" : "Excl. GST"}
                  </Label>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Prices: {pricesIncludeGst ? "incl. GST" : "excl. GST"}
                </span>
              )}
            </div>
          </div>

          {/* Row 2: compact context strip — inline pills/separators */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {selectedRequest?.vehicle_info?.rego && (
              <span className="inline-flex items-center gap-1">
                <span className="text-muted-foreground">Rego</span>
                <span className="font-semibold uppercase tracking-wide">
                  {selectedRequest.vehicle_info.rego}
                </span>
              </span>
            )}
            {(selectedRequest?.vehicle_info?.make ||
              selectedRequest?.vehicle_info?.model) && (
              <span className="inline-flex items-center gap-1">
                <span className="text-muted-foreground">Vehicle</span>
                <span className="font-medium">
                  {[
                    selectedRequest.vehicle_info.year,
                    selectedRequest.vehicle_info.make,
                    selectedRequest.vehicle_info.model,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  {selectedRequest.vehicle_info.sub_model && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {selectedRequest.vehicle_info.sub_model}
                    </span>
                  )}
                </span>
              </span>
            )}
            {selectedRequest?.tyre_specs?.width && (
              <span className="inline-flex items-center gap-1">
                <span className="text-muted-foreground">Size</span>
                <span className="font-semibold">
                  {selectedRequest.tyre_specs.width}/
                  {selectedRequest.tyre_specs.aspect_ratio ||
                    selectedRequest.tyre_specs.profile ||
                    "—"}
                  R{selectedRequest.tyre_specs.rim_size || "—"}
                </span>
              </span>
            )}
            {selectedRequest?.quantity && isTyreService && (
              <span className="inline-flex items-center gap-1">
                <span className="text-muted-foreground">Qty</span>
                <span className="font-semibold">{selectedRequest.quantity}</span>
              </span>
            )}
            {selectedRequest?.price_range && (
              <span className="inline-flex items-center gap-1">
                <span className="text-muted-foreground">Range</span>
                <span className="font-medium capitalize">
                  {selectedRequest.price_range}
                </span>
              </span>
            )}
            {selectedRequest?.brand_preference && (
              <span className="inline-flex items-center gap-1">
                <span className="text-muted-foreground">Brand pref</span>
                <span className="font-medium">{selectedRequest.brand_preference}</span>
              </span>
            )}
          </div>

          {/* Row 3: Comparison intent pill (compact, single line) */}
          {selectedRequest?.comparison_intent && (
            <div
              className={`inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border-l-4 px-2.5 py-1 text-xs ${
                selectedRequest.comparison_intent === "specific"
                  ? "border-blue-500 bg-blue-500/10"
                  : selectedRequest.comparison_intent === "budget"
                  ? "border-purple-500 bg-purple-500/10"
                  : "border-amber-500 bg-amber-500/10"
              }`}
              data-testid="shop-quote-intent-banner"
            >
              <span className="font-bold uppercase tracking-wide text-[10px] text-muted-foreground">
                Looking for
              </span>
              <span className="font-semibold">
                {selectedRequest.comparison_intent === "specific" && "Specific tyre"}
                {selectedRequest.comparison_intent === "budget" && "Budget match"}
                {selectedRequest.comparison_intent === "open" && "Open to suggestions"}
              </span>
              {selectedRequest.comparison_intent === "specific" &&
                (selectedRequest.tyre_brand || selectedRequest.tyre_model) && (
                  <span>
                    <span className="text-muted-foreground">·</span>{" "}
                    <strong>
                      {[selectedRequest.tyre_brand, selectedRequest.tyre_model]
                        .filter(Boolean)
                        .join(" ")}
                    </strong>
                  </span>
                )}
              {selectedRequest.comparison_intent === "budget" &&
                selectedRequest.driving_priority && (
                  <span>
                    <span className="text-muted-foreground">·</span>{" "}
                    <strong>
                      {{
                        long_lasting: "Long lasting",
                        quiet_ride: "Quiet ride",
                        performance: "Performance",
                        ev_efficiency: "EV efficiency",
                        wet_grip: "Wet weather grip",
                      }[selectedRequest.driving_priority] ||
                        selectedRequest.driving_priority}
                    </strong>
                  </span>
                )}
            </div>
          )}

          {selectedRequest?.customer_note && (
            <p className="text-xs leading-snug text-amber-900 dark:text-amber-200">
              <span className="font-semibold">Customer note:</span>{" "}
              {selectedRequest.customer_note}
            </p>
          )}
        </DialogHeader>

        {/* ----- BODY: edit form (mode=edit) OR review cards (mode=review) ----- */}
        {mode === "review" ? (
          <ReviewBody
            quoteForms={quoteForms}
            pricesIncludeGst={pricesIncludeGst}
            qty={selectedRequest?.quantity || 0}
            isTyreService={isTyreService}
            tyreSize={tyreSizeStr}
          />
        ) : (
          <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
          <div
            ref={scrollerRef}
            className="px-4 sm:px-6 py-4 flex flex-col md:flex-row md:snap-x md:snap-mandatory items-stretch md:items-start gap-4 md:min-w-max"
            data-testid="quote-cards-row"
          >
            {(quoteForms || []).map((q, idx) => (
              <QuoteCard
                key={idx}
                index={idx}
                quote={q}
                isTyreService={isTyreService}
                hasAlignment={hasAlignment}
                hasRotation={hasRotation}
                pricesIncludeGst={pricesIncludeGst}
                qty={selectedRequest?.quantity || 0}
                onChange={(patch) => updateSlot(idx, patch)}
                onRemove={() => removeSlot(idx)}
                canRemove={quoteForms.length > 1}
              />
            ))}

            {/* "Add another option" placeholder — full-width row on mobile,
                slim column on desktop so the horizontal deck still works. */}
            {remainingSlots > 0 && (
              <button
                type="button"
                onClick={addSlot}
                className="md:snap-center shrink-0 self-stretch w-full md:w-[180px] flex flex-col md:flex-col items-center justify-center rounded-xl border-2 border-dashed border-input hover:border-primary hover:bg-orange-50 dark:hover:bg-orange-950/30 text-muted-foreground hover:text-primary transition-colors p-4 md:p-6 min-h-[64px] md:min-h-0"
                data-testid="add-quote-slot-btn"
              >
                <Plus className="h-6 w-6 md:h-7 md:w-7 md:mb-2" />
                <span className="text-sm font-medium text-center ml-2 md:ml-0">
                  Add another option
                  <span className="text-xs text-muted-foreground ml-1 md:ml-0 md:block">({remainingSlots} left)</span>
                </span>
              </button>
            )}
          </div>

          {/* Mobile dot indicators — hidden now that mobile stacks vertically */}
        </div>
        )}

        {/* ----- FOOTER — sticky on mobile so Submit is always reachable
             even when the on-screen keyboard is open. z-index above the
             chat widget's z-40 FAB. ----- */}
        <div className="px-4 sm:px-6 py-3 border-t bg-background sm:bg-muted/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 shrink-0 sticky bottom-0 z-50 shadow-[0_-4px_10px_-8px_rgba(0,0,0,0.15)]">
          {mode === "review" ? (
            <>
              <p className="text-xs text-muted-foreground">
                Verify each quote above. Hit <strong>Back to Edit</strong> if you spot
                any typos.
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setMode("edit")}
                  disabled={submittingQuote}
                  data-testid="back-to-edit-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Edit
                </Button>
                <Button
                  onClick={handleSubmitAllQuotes}
                  disabled={submittingQuote}
                  className="bg-primary text-primary-foreground min-h-11 flex-1 sm:flex-none"
                  data-testid="confirm-submit-quotes-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  {submittingQuote ? "Submitting..." : "Confirm & Submit"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground hidden sm:block">
                {quoteForms?.length || 0}{" "}
                {(quoteForms?.length || 0) === 1 ? "quote" : "quotes"} ready • Customer will
                receive {quoteForms?.length || 0}{" "}
                {(quoteForms?.length || 0) === 1 ? "email" : "emails"}, one per quote.
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="ghost"
                  onClick={() => setQuoteDialogOpen(false)}
                  data-testid="cancel-multi-quote-btn"
                >
                  Cancel
                </Button>
                {handleSaveDraft && (
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={submittingQuote}
                    data-testid="save-draft-btn"
                  >
                    Save Draft
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (handlePreValidateQuotes && handlePreValidateQuotes()) {
                      setMode("review");
                    }
                  }}
                  disabled={submittingQuote}
                  className="bg-primary text-primary-foreground min-h-11 flex-1 sm:flex-none"
                  data-testid="submit-all-quotes-btn"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Review & Submit
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Review-mode body ----------
function ReviewBody({ quoteForms, pricesIncludeGst, qty, isTyreService, tyreSize }) {
  // Hide already-submitted and pristine-skipped slots from review
  const reviewable = (quoteForms || []).filter((q) => !q._submitted && !q._skipped);
  return (
    <div
      className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
      data-testid="review-body"
    >
      <div className="px-4 sm:px-6 py-5 flex flex-col md:flex-row items-stretch md:items-start gap-4 md:min-w-max">
        {reviewable.map((q, idx) => (
          <ShopReviewCard
            key={idx}
            quote={q}
            optionNumber={idx + 1}
            pricesIncludeGst={pricesIncludeGst}
            qty={qty}
            isTyreService={isTyreService}
            tyreSize={tyreSize}
          />
        ))}
      </div>
    </div>
  );
}

// ---------- Individual quote card ----------
function QuoteCard({
  index,
  quote,
  isTyreService,
  hasAlignment,
  hasRotation,
  pricesIncludeGst,
  qty,
  onChange,
  onRemove,
  canRemove,
}) {
  const [showMore, setShowMore] = useState(false);
  // If a validation error mentions a field inside "More details"
  // (notes / speed rating), auto-expand so the shop can see what to fix.
  useEffect(() => {
    if (!quote._error) return;
    const err = String(quote._error).toLowerCase();
    if (err.includes("note") || err.includes("speed") || err.includes("stock") || err.includes("expiry")) {
      setShowMore(true);
    }
  }, [quote._error]);
  const tyreFitment = parseFloat(quote.tyre_fitment_cost) || 0;
  const alignment = parseFloat(quote.alignment_cost) || 0;
  const rotation = parseFloat(quote.rotation_cost) || 0;
  const enteredTotal = tyreFitment + alignment + rotation;

  let subtotalExclGst = 0,
    gst = 0,
    totalInclGst = 0;
  if (enteredTotal > 0) {
    if (pricesIncludeGst) {
      totalInclGst = enteredTotal;
      subtotalExclGst = totalInclGst / 1.15;
      gst = totalInclGst - subtotalExclGst;
    } else {
      subtotalExclGst = enteredTotal;
      gst = subtotalExclGst * 0.15;
      totalInclGst = subtotalExclGst + gst;
    }
  }

  const stateClasses = quote._submitted
    ? "border-green-400 bg-green-50/60 dark:bg-green-950/30"
    : quote._error
    ? "border-red-400 bg-red-50/40 dark:bg-red-950/30"
    : "border-input bg-card";

  // Larger tap targets on mobile (h-11 = 44px), original h-9 on md+.
  const inputCls = "h-11 md:h-9 text-base md:text-sm";

  return (
    <div
      className={`md:snap-center shrink-0 w-full md:w-[320px] rounded-xl border-2 p-4 transition-colors ${stateClasses}`}
      data-testid={`quote-card-${index}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold grid place-items-center">
            {index + 1}
          </span>
          <p className="font-semibold text-sm">Quote Option {index + 1}</p>
        </div>
        {canRemove && !quote._submitted && (
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-red-600 p-1"
            data-testid={`remove-quote-card-${index}`}
            aria-label={`Remove Quote Option ${index + 1}`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {quote._submitted && (
        <div className="rounded bg-green-50 dark:bg-green-950/40 border border-green-300 dark:border-green-700 p-2 mb-3 flex items-center gap-2 text-xs">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
          <span className="text-green-800 dark:text-green-200">Submitted</span>
        </div>
      )}
      {quote._error && (
        <div className="rounded bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-700 p-2 mb-3 flex items-start gap-2 text-xs">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
          <span className="text-red-800 dark:text-red-200">{quote._error}</span>
        </div>
      )}

      <div className="space-y-3">
        {isTyreService && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Tyre Brand *</Label>
                <Input
                  className={inputCls}
                  placeholder="e.g. Michelin"
                  value={quote.tyre_brand}
                  onChange={(e) => onChange({ tyre_brand: e.target.value, _error: null })}
                  data-testid={`quote-${index}-brand`}
                />
              </div>
              <div>
                <Label className="text-xs">Tyre Model *</Label>
                <Input
                  className={inputCls}
                  placeholder="e.g. Pilot Sport 5"
                  value={quote.tyre_model}
                  onChange={(e) => onChange({ tyre_model: e.target.value, _error: null })}
                  data-testid={`quote-${index}-model`}
                />
              </div>
            </div>
          </>
        )}

        {/* Pricing */}
        {isTyreService && (
          <div>
            <Label className="text-xs">
              Total Tyre + Fitment Cost for all {qty || "N"} tyres{" "}
              {pricesIncludeGst ? "(incl. GST)" : "(excl. GST)"} *
            </Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              className={inputCls}
              value={quote.tyre_fitment_cost}
              onChange={(e) =>
                onChange({ tyre_fitment_cost: e.target.value, _error: null })
              }
              data-testid={`quote-${index}-tyre-fitment-cost`}
              placeholder={qty ? `e.g. price × ${qty} + fitting` : "Total for all tyres"}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Total price for all {qty || "N"} tyres + fitting &amp; balancing combined
            </p>
            {qty > 0 && parseFloat(quote.tyre_fitment_cost) > 0 && (
              <p className="text-[11px] text-primary mt-1" data-testid={`quote-${index}-per-tyre-echo`}>
                ≈ ${(parseFloat(quote.tyre_fitment_cost) / qty).toFixed(2)}{" "}
                {pricesIncludeGst ? "incl." : "excl."} GST per tyre fitted
              </p>
            )}
          </div>
        )}
        {hasAlignment && (
          <div>
            <Label className="text-xs">
              Alignment Cost {pricesIncludeGst ? "(incl. GST)" : "(excl. GST)"} *
            </Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              className={inputCls}
              value={quote.alignment_cost}
              onChange={(e) => onChange({ alignment_cost: e.target.value, _error: null })}
              data-testid={`quote-${index}-alignment-cost`}
            />
          </div>
        )}
        {hasRotation && (
          <div>
            <Label className="text-xs">
              Rotation Cost {pricesIncludeGst ? "(incl. GST)" : "(excl. GST)"} *
            </Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              className={inputCls}
              value={quote.rotation_cost}
              onChange={(e) => onChange({ rotation_cost: e.target.value, _error: null })}
              data-testid={`quote-${index}-rotation-cost`}
            />
          </div>
        )}

        <div>
          <Label className="text-xs">Earliest Service Date *</Label>
          <DatePicker
            value={quote.service_date_estimate}
            onChange={(date) => onChange({ service_date_estimate: date, _error: null })}
            placeholder="Select date"
            minDate={new Date()}
            required
          />
        </div>

        {/* Live total (kept above fold — shops paste from another system) */}
        <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 p-3 text-xs space-y-1">
          {enteredTotal > 0 ? (
            <>
              <div className="flex justify-between">
                <span>Subtotal (excl. GST)</span>
                <span>${subtotalExclGst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>GST (15%)</span>
                <span>${gst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-base text-primary border-t border-orange-200 dark:border-orange-800 pt-1 mt-1">
                <span>Total</span>
                <span data-testid={`quote-${index}-total`}>${totalInclGst.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-1">
              Enter prices above to see total
            </p>
          )}
        </div>

        {/* Advanced / optional fields — collapsed by default. Kept in DOM so
            validation still sees them; wrapped in <details> so mobile users
            can skip past them without scrolling walls of inputs. */}
        <details
          open={showMore}
          onToggle={(e) => setShowMore(e.currentTarget.open)}
          className="rounded-lg border border-dashed"
        >
          <summary
            className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground min-h-11 flex items-center"
            data-testid={`quote-${index}-more-details-toggle`}
          >
            {showMore ? "Hide" : "More details"} — notes, speed rating, stock, expiry
          </summary>
          <div className="px-3 pb-3 pt-1 space-y-3">
            {isTyreService && (
              <div>
                <Label className="text-xs">Speed Rating *</Label>
                <Input
                  className={inputCls}
                  placeholder="e.g. W XL"
                  value={quote.speed_rating}
                  onChange={(e) =>
                    onChange({
                      speed_rating: e.target.value
                        .slice(0, 8)
                        .replace(/[^A-Za-z0-9 ]/g, ""),
                    })
                  }
                  maxLength={8}
                  data-testid={`quote-${index}-speed-rating`}
                />
              </div>
            )}
            {isTyreService && (
              <div>
                <Label className="text-xs">Stock Availability</Label>
                <select
                  className="flex h-11 md:h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base md:text-sm shadow-sm"
                  value={quote.stock_availability}
                  onChange={(e) => onChange({ stock_availability: e.target.value })}
                  data-testid={`quote-${index}-stock`}
                >
                  <option value="in_stock">In stock</option>
                  <option value="order_required">Order required</option>
                  <option value="limited">Limited stock</option>
                </select>
              </div>
            )}
            {isTyreService && (
              <div>
                <Label className="text-xs">Quote Expiry</Label>
                <DatePicker
                  value={quote.quote_expiry}
                  onChange={(date) => onChange({ quote_expiry: date })}
                  placeholder="14 days default"
                  minDate={new Date()}
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Notes to Customer *</Label>
              <Textarea
                rows={3}
                className="text-base md:text-sm"
                placeholder="Why this option is great for the customer..."
                value={quote.notes}
                onChange={(e) => onChange({ notes: e.target.value, _error: null })}
                data-testid={`quote-${index}-notes`}
              />
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
